import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, CalendarClock, Zap, Droplet, Tent, Car, Check, Send } from "lucide-react";

const Feat = ({ ok, icon: Icon, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border ${ok ? "bg-emerald-950/30 text-emerald-300 border-emerald-800/40" : "bg-[#181A20] text-stone-500 border-[#272A34] line-through"}`}><Icon className="w-3.5 h-3.5" /> {label}</span>
);

export default function OpportunityDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { data: o, isLoading } = useQuery({ queryKey: ["opportunity", slug], queryFn: async () => (await api.get(`/opportunities/${slug}`)).data });
  const [app, setApp] = useState({ message: "", website: "", product_type: "", dimensions: "", electricity: false, availability: "", technical_needs: "" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setApp((s) => ({ ...s, [k]: v }));

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + `/opportunites/${slug}`)}`;
  };
  const apply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post(`/opportunities/${slug}/apply`, app); setSent(true); toast.success("Candidature envoyée !"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  if (isLoading) return <div className="py-24 text-center text-stone-400">…</div>;
  if (!o) return <div className="py-24 text-center text-stone-400">404</div>;

  const inp = "w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-4 py-2.5 text-sm text-stone-200 outline-none focus:border-amber-500/50";

  return (
    <>
      <Seo title={o.title} description={(o.description || "").slice(0, 160)} image={o.image} canonicalPath={`/opportunites/${slug}`} ogType="article" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <Link to="/opportunites" data-testid="opp-back" className="inline-flex items-center gap-1.5 text-stone-400 hover:text-amber-400 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Appels à participation</Link>
        <div className="font-mono text-[10px] uppercase tracking-wider text-amber-500 mb-2">🔥 Recherche exposants</div>
        <h1 className="font-serif text-4xl text-amber-100 mb-2">{o.title}</h1>
        <div className="text-stone-400 mb-6">{o.organization}{o.event_title ? ` · ${o.event_title}` : ""}</div>

        <div className="grid sm:grid-cols-2 gap-3 mb-8 text-sm">
          {(o.city || o.location) && <div className="flex items-center gap-2 text-stone-300"><MapPin className="w-4 h-4 text-amber-500" /> {o.location || o.city}{o.address ? `, ${o.address}` : ""}{o.city && o.location ? `, ${o.city}` : ""}{o.canton ? ` (${o.canton})` : ""}, {o.country}</div>}
          {o.event_date && <div className="flex items-center gap-2 text-stone-300"><CalendarClock className="w-4 h-4 text-amber-500" /> Événement : {o.event_date}</div>}
          {o.deadline && <div className="flex items-center gap-2 text-stone-300"><CalendarClock className="w-4 h-4 text-amber-500" /> Date limite : {o.deadline.slice(0, 10)}</div>}
          {o.spots != null && <div className="text-stone-300">Exposants recherchés : {o.spots}</div>}
          {o.stand_price && <div className="text-stone-300">Tarif emplacement : {o.stand_price}</div>}
          {o.dimensions && <div className="text-stone-300">Dimensions : {o.dimensions}</div>}
        </div>

        {o.categories?.length > 0 && <div className="mb-6"><div className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-2">Recherche</div><div className="flex flex-wrap gap-2">{o.categories.map((c) => <span key={c} className="px-3 py-1 rounded-full text-sm bg-[#121418] text-stone-300 border border-[#272A34]">{c}</span>)}</div></div>}

        <div className="flex flex-wrap gap-2 mb-8">
          <Feat ok={o.electricity} icon={Zap} label="Électricité" />
          <Feat ok={o.water} icon={Droplet} label="Eau" />
          <Feat ok={o.camping} icon={Tent} label="Camping" />
          <Feat ok={o.parking} icon={Car} label="Parking" />
        </div>

        {o.description && <p className="text-stone-300/90 leading-relaxed whitespace-pre-line mb-6">{o.description}</p>}
        {o.conditions && <div className="mb-8 p-4 rounded-lg bg-[#121418] border border-[#272A34]"><div className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-2">Conditions</div><p className="text-sm text-stone-400 whitespace-pre-line">{o.conditions}</p></div>}

        {/* APPLY */}
        <div className="mt-6 border-t border-amber-900/30 pt-8" id="apply">
          <h2 className="font-serif text-2xl text-stone-100 mb-4">Candidater</h2>
          {o.expired ? (
            <div className="rounded-lg border border-[#272A34] bg-[#121418] p-6 text-stone-400" data-testid="opp-closed">Les candidatures sont terminées.</div>
          ) : !user ? (
            <button onClick={handleLogin} data-testid="opp-apply-login" className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">Se connecter pour candidater</button>
          ) : sent ? (
            <div className="rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-6 text-center" data-testid="opp-apply-success"><Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" /><p className="text-stone-200">Votre candidature a bien été transmise à l'organisateur.</p></div>
          ) : (
            <form onSubmit={apply} className="space-y-4 bg-[#121418] border border-[#272A34] rounded-xl p-6" data-testid="opp-apply-form">
              <textarea className={`${inp} min-h-[110px]`} placeholder="Votre message à l'organisateur" data-testid="opp-apply-message" value={app.message} onChange={(e) => set("message", e.target.value)} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inp} placeholder="Site / boutique" data-testid="opp-apply-website" value={app.website} onChange={(e) => set("website", e.target.value)} />
                <input className={inp} placeholder="Type de produits/services" value={app.product_type} onChange={(e) => set("product_type", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 items-center">
                <input className={inp} placeholder="Dimensions souhaitées" value={app.dimensions} onChange={(e) => set("dimensions", e.target.value)} />
                <label className="flex items-center gap-2 text-sm text-stone-400"><input type="checkbox" checked={app.electricity} onChange={(e) => set("electricity", e.target.checked)} className="accent-amber-500" /> Besoin électrique</label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inp} placeholder="Disponibilité (dates)" data-testid="opp-apply-availability" value={app.availability} onChange={(e) => set("availability", e.target.value)} />
                <input className={inp} placeholder="Besoins techniques / logistiques" data-testid="opp-apply-technical" value={app.technical_needs} onChange={(e) => set("technical_needs", e.target.value)} />
              </div>
              <button type="submit" disabled={saving} data-testid="opp-apply-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md border border-amber-400/30 disabled:opacity-60"><Send className="w-4 h-4" /> {saving ? "…" : "Envoyer ma candidature"}</button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
